import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import Article from './Article';
import PageMetadata from '../seo/MetaHead';
import { ORIGIN } from '../seo/pageMetadata';
import { excerpt } from './excerpt';

const API = 'https://blaketechnologiessite.onrender.com/api/public/blog';
// Fetch public data without employee credentials. Do not reuse management state.
function usePublicBlog(path) {
    const [attempt, setAttempt] = useState(0);
    const [result, setResult] = useState({});
    useEffect(() => {
        const refresh = () => setAttempt(value => value + 1);
        const visible = () => { if (document.visibilityState === 'visible') refresh(); };
        window.addEventListener('focus', refresh);
        window.addEventListener('pageshow', refresh);
        document.addEventListener('visibilitychange', visible);
        return () => {
            window.removeEventListener('focus', refresh);
            window.removeEventListener('pageshow', refresh);
            document.removeEventListener('visibilitychange', visible);
        };
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        let active = true;
        setResult({ path, attempt, loading: true });
        fetch(`${API}${path}`, { signal: controller.signal, credentials: 'omit', cache: 'no-store' })
            .then(async response => {
                if (!response.ok) throw Object.assign(new Error(), { status: response.status });
                return response.json();
            }).then(data => { if (active) setResult({ path, attempt, data }); })
            .catch(error => { if (active) setResult({ path, attempt, error: error.status === 404 || error.status === 400 ? 'missing' : 'unavailable' }); });
        return () => { active = false; controller.abort(); };
    }, [path, attempt]);
    return { ...(result.path === path && result.attempt === attempt ? result : { loading: true }), retry: () => setAttempt(value => value + 1) };
}
function Status({ state }) {
    if (state.loading) return <p role="status">Loading article…</p>;
    return <section className="blog-message">
        <h1>{state.error === 'missing' ? 'Article not found' : 'Articles temporarily unavailable'}</h1>
        <p role={state.error === 'missing' ? undefined : 'alert'}>{state.error === 'missing' ? 'This page may have moved or is no longer available.' : 'We could not load the blog right now. Please try again.'}</p>
        <div className="blog-actions">{state.error !== 'missing' && <button onClick={state.retry}>Try again</button>}<Link to="/">Visit Blake Technologies</Link></div>
    </section>;
}
function PageFrame({ children }) {
    const { pathname, search } = useLocation();
    const main = useRef(null);
    useEffect(() => { main.current?.focus({ preventScroll: true }); }, [pathname, search]);
    return <main className="public-blog" tabIndex={-1} ref={main}>{children}</main>;
}
export function PublicArticle() {
    const { slug } = useParams();
    const state = usePublicBlog(`/${encodeURIComponent(slug)}`);
    const post = state.data;
    const meta = post ? { title: post.metaTitle?.trim() || `${post.title} | Blake Technologies`, description: post.metaDescription?.trim() || excerpt(post), canonical: `${ORIGIN}/blog/${post.slug}`, type: 'article' } : null;
    return <PageMetadata meta={meta}><PageFrame>{post ? <Article post={post} /> : <Status state={state} />}</PageFrame></PageMetadata>;
}
export function LegacyArticle() {
    const { blogId } = useParams();
    const state = usePublicBlog(`/legacy/${encodeURIComponent(blogId)}`);
    return state.data ? <Navigate replace to={`/blog/${state.data.slug}`} /> : <PageFrame><Status state={state} /></PageFrame>;
}
